<template>
  <div id="lom-events-monthly-list">
    <v-overlay :value="OverlayLoadingFlag">
      <div class="text-center overlay-content">
        <p class="overlay-text">
          Please wait, your Report is getting generated. Loading time may vary
          depending on the number of events you have uploaded.
        </p>
        <v-progress-circular indeterminate size="64" color="white"></v-progress-circular>
      </div>
    </v-overlay>

    <!-- Professional Header Section -->
    <v-card class="header-card elevation-6 rounded-lg mb-6">
      <v-sheet class="list-header" tile>
        <div class="header-top">
          <!-- Top Left: Title with Icon -->
          <div class="header-title-section">
            <div class="title-icon-wrapper">
              <v-icon size="40" class="white--text title-icon">mdi-calendar-multiple</v-icon>
            </div>
            <div class="title-content">
              <h1 class="page-title">{{ PageTitle }}</h1>
              <p class="page-desc"><v-icon size="16" class="mr-1">mdi-clock</v-icon>{{ YearName }} - {{ PageDescription
              }}</p>
            </div>
          </div>

          <!-- Top Right: Actions -->
          <div class="header-actions-top">
            <v-btn @click.prevent="refreshPageData" color="white" icon class="action-btn" v-tooltip="'Refresh List'">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>

            <router-link to="/lom-events/create" class="text-decoration-none">
              <v-btn color="#1b5e20" dark elevation="6" class="action-btn-primary create-event-btn" large>
                <v-icon class="mr-2">mdi-plus-circle</v-icon> Create Event
              </v-btn>
            </router-link>
          </div>
        </div>
      </v-sheet>
    </v-card>

    <!-- Main Content -->
    <v-container fluid class="px-6 py-6">
      <!-- Speed Dial -->
      <v-speed-dial v-model="fab" :top="top" :bottom="bottom" :right="right" :left="left" :direction="direction"
        :open-on-hover="hover" :transition="transition" :fixed="true" v-if="SpeedDialFlag">
        <template v-slot:activator>
          <v-btn v-model="fab" color="primary" dark fab class="speed-dial-btn">
            <v-icon v-if="fab"> mdi-close </v-icon>
            <v-icon v-else> mdi-plus </v-icon>
          </v-btn>
        </template>
        <v-btn fab dark small color="indigo" @click.prevent="addRecord" class="speed-dial-item">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-speed-dial>

      <!-- Filter Card -->
      <v-card class="filter-card mb-6" elevation="2" v-if="RoleId == 9">
        <v-card-title class="filter-title py-4">
          <v-icon class="mr-2 primary--text">mdi-filter-variant</v-icon>
          <span class="primary--text font-weight-bold">Filters & Actions</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-6">
          <v-row wrap align="center">
            <v-col cols="12" md="4" v-if="RoleId == 9" class="pa-2">
              <div class="filter-field-wrapper">
                <label class="filter-label">
                  <v-icon size="18" class="mr-2">mdi-calendar-month</v-icon>
                  Select Year
                </label>
                <v-autocomplete v-model="JciYearCode" :items="YearCodeOptions" item-text="text" item-value="value"
                  outlined dense rounded clearable :loading="YearCodeOptionsLoading" placeholder="Choose a year..."
                  prepend-inner-icon="mdi-calendar" @change="searchForm" class="filter-input"></v-autocomplete>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <!-- Loading Spinner -->
      <div v-if="LoadingFlag" class="loading-container">
        <v-skeleton-loader type="table" v-bind="attrs"></v-skeleton-loader>
      </div>
      <v-dialog eager v-model="previewRecordPrompt" v-if="previewRecordPrompt" max-width="90%">
        <pdf-document-file-preview :PageTitle="PreviewPageTitle" :PreviewButtonFlag="previewRecordPrompt"
          :Documentpath="DocumentFilePath" @hideDialog="hidePreviewRecordPrompt"
          v-if="previewRecordPrompt"></pdf-document-file-preview>
      </v-dialog>
      <v-container class="py-0" v-if="tableData1.length == 0 && LoadingFlag == false">
        <v-row wrap>
          <v-col align="center" cols="12" md="12">
            <v-card class="empty-state" elevation="0">
              <v-card-text class="text-center py-8">
                <v-icon size="80" color="primary" class="mb-4">mdi-folder-open-outline</v-icon>
                <h3 class="text-h6 primary--text mb-2">No events found</h3>
                <p class="text-subtitle2 grey--text">
                  No events available for the selected criteria. Create a new event to get started.
                </p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
      <v-card class="data-card" elevation="2" v-if="tableData1.length > 0">
        <v-card-title class="card-header-title py-4">
          <v-icon class="mr-2 white--text">mdi-table</v-icon>
          <span class="white--text">Events Summary ({{ tableData1.length }} records)</span>
          <v-spacer></v-spacer>
          <json-excel v-if="
            tableData1.length > 0 &&
            tableOptions1.JsonDownloadButtonFlag == true
          " :name="tableOptions1.ExcelFileName" :data="tableData1" :fields="tableOptions1.JsonExcelFields">
            <v-btn icon small color="white" class="mx-1" title="Download Excel">
              <v-icon>mdi-download-circle</v-icon>
            </v-btn>
          </json-excel>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <v-data-table class="data-table-professional elevation-0" loading-text="Loading... Please wait"
            v-model="selected" :search="search" :loading="TableLoadingFlag" :item-key="tableOptions1.ItemKey"
            :single-select="tableOptions1.SingleSelectFlag" :headers="tableColumns1" :items="tableData1"
            :items-per-page="tableOptions1.ItemsPerPage" :footer-props="tableOptions1.FooterProps"
            :hide-default-footer="tableOptions1.HideDefaultFooter">
            <template v-slot:item.MrfEndDateTxt="{ item }">
              <v-chip :color="item.MrfEndDateColor" draggable dark>{{
                item.MrfEndDateTxt
                }}</v-chip>
            </template>
            <template v-slot:item.ActiveStatusTxt="{ item }">
              <v-chip :color="getActiveStatusColor(item.ActiveStatusTxt)" draggable dark>{{ item.ActiveStatusTxt
              }}</v-chip>
            </template>
            <template v-slot:item.ProfilePic="{ item }">
              <img width="100" height="100" :src="item.ProfilePic" />
            </template>
            <template v-slot:item.MonthlyEventsFlag="{ item }">
              <v-tooltip bottom v-if="item.MonthlyEventsFlag && item.Total > 0">
                <template v-slot:activator="{ on, attrs }">
                  <!-- <v-icon
                        icon
                        size="25px"
                        color="primary"
                        @click="editData(item, e)"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-file-find
                      </v-icon> -->
                  <v-btn v-if="item.MonthlyEventsFlag && item.Total > 0" @click="editData(item, e)" target="_blank"
                    color="primary" small class="text-white" v-bind="attrs" v-on="on">
                    <v-icon dark> mdi-file-find </v-icon> Events
                  </v-btn>
                </template>
                <span> {{ item.MonthName }} Events </span>
              </v-tooltip>
            </template>
            <template v-slot:item.FileGenerateFlag="{ index, item }">
              <v-tooltip bottom v-if="item.FileGenerateFlag && item.Total > 0">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-if="item.MonthlyEventsFlag && item.Total > 0" @click="generateReport(index, item)"
                    target="_blank" color="red" small class="text-white" v-bind="attrs" v-on="on">
                    <v-icon dark> mdi-file-pdf </v-icon> Generate
                  </v-btn>
                </template>
                <span> Generate {{ item.MonthName }} MRF </span>
              </v-tooltip>
            </template>
            <template v-slot:item.FilePreviewFlag="{ item }">
              <v-tooltip bottom v-if="item.FilePreviewFlag && item.FilePath != ''">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-if="item.FilePath != '' && item.FilePreviewFlag == 1" @click="previewData(item, e)"
                    target="_blank" color="blue" small class="text-white" v-bind="attrs" v-on="on">
                    <v-icon dark> mdi-file-search </v-icon> Preview
                  </v-btn>
                </template>
                <span> {{ item.MonthName }} MRF Preview </span>
              </v-tooltip>
              <br />
              <small> {{ item.LastUpdatedAtTxt }} </small>
            </template>
            <template v-slot:item.FileDownloadFlag="{ item }">
              <v-tooltip bottom v-if="item.FileDownloadFlag && item.FilePath != ''">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn v-if="item.FilePath != '' && item.FileDownloadFlag == 1" :href="item.FilePath" target="_blank"
                    color="#2fb65a" small class="text-white" v-bind="attrs" v-on="on">
                    <v-icon dark> mdi-download </v-icon> Download
                  </v-btn>
                </template>
                <span> {{ item.MonthName }} MRF Download </span>
              </v-tooltip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom v-if="item.DeleteFlag">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon icon size="25px" color="red" @click="deleteConfirmAlert(item, e)" v-bind="attrs" v-on="on">
                    mdi-delete
                  </v-icon>
                </template>
                <span> Delete </span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Action Buttons Section -->
      <v-row wrap class="mt-6 mb-4" v-if="tableData1.length > 0">
        <v-col cols="12" class="d-flex justify-center gap-2">
          <v-btn v-if="ExcelDownloadFlag == true && ExcelFileFlag == false" @click.prevent="generateReportExcel"
            :loading="ExcelLoadingFlag" color="warning" elevation="2" rounded class="action-button">
            <v-icon class="mr-1">mdi-file-export</v-icon> Generate Full Report
          </v-btn>
          <v-btn v-if="ExcelFileName != '' && ExcelFileFlag == true" :href="ExcelFileName" target="_blank"
            color="success" elevation="2" rounded class="action-button">
            <v-icon class="mr-1">mdi-download-circle</v-icon> Download Report
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
    <br /><br />
    </v-container>
  </div>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import companyConfig from "@/company_config.json";
import {
  ADD_BODY_CLASSNAME,
  REMOVE_BODY_CLASSNAME,
} from "@/core/services/store/htmlclass.module.js";
import Swal from "sweetalert2";
import PdfDocumentFilePreview from "../../../../components/PdfDocumentFilePreview.vue";

export default {
  mixins: [common],
  components: { PdfDocumentFilePreview },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SearchFlag: false,
      SubmitFlag: false,
      SpeedDialFlag: false,
      TableLoadingFlag: false,
      OverlayLoadingFlag: false,

      ExcelDownloadFlag: false,
      ExcelLoadingFlag: false,
      ExcelFileFlag: false,

      rows: [],

      Modules: {},

      tableData1: [],
      tableColumns1: [],
      tableOptions1: [],
      selected: [],
      search: "",
      CurrentYearId: 0,

      DownloadFlag: 0,
      ExcelFileName: "",
      excel_fields: {
        "LO Id": "LomId",
        "Zone Id": "ZoneId",
        "LO Name": "LomName",
        "President Name": "LomPresidentFullName",
        "LO NAME": "LomName",
        "Mobile No": "LomPresidentMobileNo",
        "Email Id": "LomPresidentEmailid",
      },

      recordToEdit: "",
      record: {},
      addRecordPrompt: false,
      editRecordPrompt: false,
      previewRecordPrompt: false,
      editPageDescription: "Assign LO President for JCI India",

      DocumentFilePath: "",
      PreviewPageTitle: "MRF File Preview",

      selectedData: {},

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      ZoneCode: "",
      ZoneName: "",

      LomCode: "",
      LomName: "",
      RoleId: "",
      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
      YearCodeOptions: [],
      YearCodeOptionsLoading: false,
    };
  },
  mounted() {
    /*
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Vuetify", route: "alerts" },
      { title: "Form Inputs & Control", route: "autocompletes" },
      { title: "Fileinptus" },
    ]);
    */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;
        this.JciYearCode = tr.CurrentJciYearId;
        this.YearName = tr.CurrentJciYearName;
        this.getYearCodeOptions();
        this.searchForm();

        var RoleId = this.$session.get("RoleId");
        RoleId = RoleId == (null || undefined) ? "" : RoleId;
        console.log({ RoleId: RoleId });
        this.RoleId = RoleId;
        this.YearCodeOptionsLoading = true;
      }
    },
    YearCodeOptions: function () {
      console.log("watch YearCodeOptions");
      this.YearCodeOptionsLoading = false;
    },
  },
  methods: {
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    getYearCodeOptions() {
      console.log("getYearCodeOptions called");
      this.YearCodeOptionsLoading = true;
      var selectbox1_source = "JciYearCode";
      var selectbox1_destination = "YearCodeOptions";
      var selectbox1_url = "api/jci-year/options";
      var selectbox1_conditions_array = {
        UserInterface: 5,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    addRecord() {
      console.log("addRecord called");
      var url = "/lom/create";
      var params = {};
      this.pageRedirect(params, url);
    },
    editData(tr) {
      console.log("editData called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        var url = "/lom-events/list";
        var year = tr.YearCode;
        console.log({ year });
        var month = tr.MonthId;
        console.log({ month });
        var params = {
          year: year,
          month: month,
        };
        this.pageRedirect(params, url);
      } else {
        var message = "Kindly select one event to edit";
        this.toast("error", message);
      }
    },
    resetForm() {
      this.$refs.form2.reset();
      this.tableData1 = [];
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    hideAddRecordPrompt() {
      console.log("hideAddRecordPrompt called");
      this.addRecordPrompt = false;
      this.searchForm();
    },
    hideEditRecordPrompt() {
      console.log("hideEditRecordPrompt called");
      this.editRecordPrompt = false;
      this.searchForm();
    },
    editAlert(tr) {
      console.log("editAlert called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        var LomId = tr.LomId;
        console.log("LomId=" + LomId);
        this.record = tr;
        this.recordToEdit = LomId;
        this.editPageDescription = "Assign 2021 LO President for " + tr.LomName;
        this.editRecordPrompt = true;
      } else {
        var message = "Kindly select one record to modifiy";
        this.toast("error", message);
      }
    },
    hidePreviewRecordPrompt() {
      console.log("hidePreviewRecordPrompt called");
      this.previewRecordPrompt = false;
    },
    previewData(tr) {
      console.log("previewData called");
      var message = "";
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      console.log({ tr });
      if (n1 > 0) {
        var FilePath = tr.FilePath;
        console.log({ FilePath });
        var MonthName = tr.MonthName;
        console.log({ MonthName });
        if (FilePath != "") {
          this.previewRecordPrompt = true;
          this.DocumentFilePath = FilePath;
          this.PreviewPageTitle = MonthName + " MRF Preview";
        } else {
          message = "File should not be empty.";
          this.toast("error", message);
        }
      } else {
        message = "Kindly select one record to preview";
        this.toast("error", message);
      }
    },
    refreshPageData() {
      console.log("refreshPageData called");

      var ZoneName = this.$session.get("ZoneName");
      ZoneName = ZoneName == (null || undefined) ? "" : ZoneName;
      console.log("ZoneName=" + ZoneName);
      this.ZoneName = ZoneName;

      var ZoneCode = this.$session.get("ZoneId");
      ZoneCode = ZoneCode == (null || undefined) ? "" : ZoneCode;
      console.log("ZoneCode=" + ZoneCode);
      this.ZoneCode = ZoneCode;

      var LomCode = this.$session.get("LomId");
      LomCode = LomCode == (null || undefined) ? "" : LomCode;
      console.log("LomCode=" + LomCode);
      this.LomCode = LomCode;

      var LomName = this.$session.get("LomName");
      LomName = LomName == (null || undefined) ? "" : LomName;
      console.log("LomName=" + LomName);
      this.LomName = LomName;

      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "lom_events",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    searchForm() {
      console.log("searchForm is called");

      var message = "";

      // var validate1 = this.$refs.form2.validate();
      // console.log("validate1=" + validate1);

      var YearCode = this.JciYearCode;
      console.log({ YearCode });

      var LomCode = this.LomCode;
      console.log({ LomCode });

      if (YearCode != "" && LomCode != "") {
        this.LoadingFlag = true;
        this.tableData1 = [];
        this.ExcelDownloadFlag = false;
        this.ExcelFileName = "";
        this.ExcelFileFlag = false;
        this.Modules = {};

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-events/month-list";
        var upload = {
          UserInterface: 1,
          Year: YearCode,
          Lom: LomCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
          server_url +
          ", token=" +
          token +
          ", add_url=" +
          add_url
        );

        const thisIns = this;
        var output = "";
        var records = {};
        var table = {};
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            console.log({ records });

            if (flag == 1) {
              var options = records.TableOptions;
              console.log({ options });

              thisIns.tableColumns1 = records.TableHeader;
              thisIns.tableOptions1 = options;
              thisIns.ExcelDownloadFlag = options.ExcelDownloadButtonFlag;
              thisIns.tableData1 = records.TableData;
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
          });
      } else {
        if (YearCode == "") {
          message += "Year should not be empty. ";
        }
        if (LomCode == "") {
          message += "LO should not be empty. ";
        }
        this.toast("error", message);
      }
    },
    generateReport(index, tr) {
      console.log("generateReport is called");
      var message = "";
      console.log({ index });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      console.log({ tr });
      // n1 = 0;
      if (n1 > 0) {
        // var filepath = this.tableData1[index].FilePath;
        var filepath =
          "http://192.168.1.101/projects/jci_india_erp/jci_india_erp_public/lib/assets/export/mrf/2021/848/jci_months.csv";
        console.log({ filepath });
        this.tableData1[index].FilePath = "";
        this.tableData1[index].FileDownloadFlag = false;
        this.tableData1[index].FilePreviewFlag = false;
        this.tableData1[index].LastUpdatedAtTxt = "";
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-events/generate-monthly-report";
        var upload = {
          UserInterface: 1,
          Year: tr.YearCode,
          Month: tr.MonthId,
          Lom: tr.LomCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
          server_url +
          ", token=" +
          token +
          ", add_url=" +
          add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()

            thisIns.OverlayLoadingFlag = false;
            thisIns.searchForm();

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            thisIns.tableData1[index].FileDownloadFlag =
              records.FileDownloadFlag;
            thisIns.tableData1[index].FilePreviewFlag =
              records.FileDownloadFlag;
            thisIns.tableData1[index].FilePath = records.FilePath;

            if (flag == 1) {
              thisIns.tableData1[index].FileDownloadFlag =
                records.FileDownloadFlag;
              thisIns.tableData1[index].FilePreviewFlag =
                records.FileDownloadFlag;
              thisIns.tableData1[index].FilePath = records.FilePath;
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      } else {
        message = "Kindly select one month to generate report";
        this.toast("error", message);
      }
    },
    deleteConfirmAlert(tr) {
      console.log("deleteConfirmAlert called");
      var message = "";
      // var tr = this.selected
      // var n1 = tr.length
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        var htmlTxt = "";
        htmlTxt += "<br/> Event Type <b> " + tr.OrganizationTypeName + "</b>";
        htmlTxt += "<br/> Portfolio <b> " + tr.PortfolioShortCode + "</b>";
        htmlTxt += "<br/> Event name <b> " + tr.JciEventName + "</b>";

        Swal.fire({
          title: "Do you want to continue?",
          text: "Please verify all the information before proceeding.",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#d33",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#3085d6",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.deleteData(tr.JciEventId);
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        if (n1 == 0) {
          message += "Kindly select one record to delete. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    deleteData(EventId) {
      console.log("deleteData is called");

      console.log("EventId=" + EventId);

      if (EventId > 0) {
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-events/destroy";
        var upload = {
          UserInterface: 1,
          EventId: EventId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
          server_url +
          ", token=" +
          token +
          ", add_url=" +
          add_url
        );

        const thisIns = this;
        var output = "";
        var records = {};
        var table = {};
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.OverlayLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            table = response.data.TableContent;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              thisIns.sweetAlert("success", output, true);
              thisIns.searchForm();
            } else {
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "Event Id should not be empty. ";
        this.sweetAlert("error", message, false);
      }
    },
  },
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss" scoped>
/* Keyframe Animations */
@keyframes glow {

  0%,
  100% {
    box-shadow: 0 0 10px rgba(255, 255, 255, 0.3),
      0 0 20px rgba(25, 118, 210, 0.3);
  }

  50% {
    box-shadow: 0 0 15px rgba(255, 255, 255, 0.5),
      0 0 30px rgba(25, 118, 210, 0.5);
  }
}

@keyframes shimmer {
  0% {
    background-position: -1000px 0;
  }

  100% {
    background-position: 1000px 0;
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

#lom-events-monthly-list {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8eef7 100%);
  min-height: 100vh;
  animation: slideInUp 0.6s ease-out;

  /* Header Card */
  .header-card {
    background: white;
    border: none;
    border-radius: 16px !important;
    overflow: hidden;
    box-shadow: 0 12px 35px rgba(25, 118, 210, 0.15);
    margin-bottom: 24px;
  }

  .list-header {
    background: linear-gradient(135deg, #05213d 0%, #1663a3 50%, #122442 100%);
    padding: 24px 28px;
    border-radius: 16px;
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.15);

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 500px;
      height: 500px;
      background: radial-gradient(circle,
          rgba(255, 255, 255, 0.1) 0%,
          transparent 70%);
      animation: spin 15s linear infinite;
    }

    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.05) 50%,
          transparent 100%);
      animation: shimmer 3s infinite;
    }
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(360deg);
    }
  }

  .header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
    position: relative;
    z-index: 2;
  }

  .header-title-section {
    display: flex;
    align-items: center;
    gap: 18px;
    flex: 1;
    min-width: 0;
  }

  .title-icon-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.15);
    border-radius: 14px;
    padding: 12px;
    flex-shrink: 0;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.25);
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.25);
      animation: glow 2s ease-in-out infinite;
    }

    .title-icon {
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.15));
    }
  }

  .title-content {
    min-width: 0;
  }

  .page-title {
    margin: 0;
    font-size: 26px;
    font-weight: 800;
    color: #ffffff;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    letter-spacing: -0.5px;
  }

  .page-desc {
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
    font-weight: 500;
    margin-top: 6px;
    margin-bottom: 0;
    letter-spacing: 0.3px;
    display: flex;
    align-items: center;
  }

  .header-actions-top {
    display: flex;
    align-items: center;
    gap: 12px;
    white-space: nowrap;
    flex-wrap: wrap;
    justify-content: flex-end;
    position: relative;
    z-index: 2;
  }

  .action-btn {
    color: white !important;
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;

    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 50%;
      transform: translate(-50%, -50%) scale(0);
      transition: transform 0.6s ease;
    }

    &:hover {
      transform: scale(1.15) rotate(5deg);
      box-shadow: 0 4px 12px rgba(255, 255, 255, 0.2);

      &::before {
        transform: translate(-50%, -50%) scale(1);
      }
    }

    &:active {
      transform: scale(0.95);
    }
  }

  .action-btn-primary {
    background: rgba(255, 255, 255, 0.18) !important;
    color: #ffffff !important;
    border-radius: 8px;
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    border: 1px solid rgba(255, 255, 255, 0.25) !important;
    backdrop-filter: blur(8px);
    font-weight: 600;
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.2) 50%,
          transparent 100%);
      transition: left 0.5s ease;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.28) !important;
      transform: translateY(-3px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
      border-color: rgba(255, 255, 255, 0.35) !important;

      &::before {
        left: 100%;
      }
    }

    &:active {
      transform: translateY(-1px);
    }
  }

  /* Overlay */
  .overlay-content {
    background: rgba(0, 0, 0, 0.8);
    padding: 40px;
    border-radius: 12px;
    backdrop-filter: blur(10px);

    .overlay-text {
      color: white;
      font-size: 16px;
      margin-bottom: 20px;
    }
  }

  /* Filter Card */
  .filter-card {
    background: linear-gradient(to right, #ffffff 0%, #f9fbff 100%);
    border: 1px solid #e3e8f3;
    border-radius: 12px;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 8px 24px rgba(22, 99, 163, 0.15);
      transform: translateY(-2px);
    }

    .filter-title {
      background: linear-gradient(135deg, #f0f5ff 0%, #f9fbff 100%);
      border-radius: 12px 12px 0 0;
    }

    .filter-field-wrapper {
      .filter-label {
        display: flex;
        align-items: center;
        font-weight: 600;
        color: #1663a3;
        margin-bottom: 12px;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .filter-input {
        ::v-deep {
          .v-input__control {
            border-radius: 8px;
          }

          .v-field__outline {
            color: #e0e8f5;
          }

          &:hover .v-field__outline {
            color: #1663a3;
          }

          &.v-input--is-focused .v-field__outline {
            color: #1663a3 !important;
          }

          .v-field__input {
            color: #333;
            font-weight: 500;
          }

          .v-icon {
            color: #1663a3 !important;
          }
        }
      }
    }
  }

  /* Data Card */
  .data-card {
    border-radius: 12px;
    overflow: hidden;
    border: none;
    background: white;

    .card-header-title {
      background: linear-gradient(135deg, #1663a3 0%, #0d4a8a 100%);
      display: flex;
      align-items: center;
    }

    .data-table-professional {
      ::v-deep {
        .v-data-table__wrapper {
          border-radius: 0 0 12px 12px;
        }

        .v-data-table-header {
          background: linear-gradient(135deg, #f0f5ff 0%, #e8eef7 100%);

          th {
            color: #1663a3 !important;
            font-weight: 700;
            font-size: 13px;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            border-bottom: 2px solid #d4dff0;
            padding: 16px !important;
          }
        }

        tbody {
          tr {
            border-bottom: 1px solid #f0f3f8;
            transition: all 0.2s ease;

            &:hover {
              background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);
              box-shadow: inset 0 0 8px rgba(22, 99, 163, 0.08);

              td {
                color: #05213d;
              }
            }

            &:nth-child(even) {
              background: linear-gradient(135deg, #ffffff 0%, #f5f8ff 100%);
            }

            td {
              padding: 14px 16px !important;
              color: #555;
              font-size: 13px;
              transition: color 0.2s ease;

              .v-btn {
                transition: all 0.3s ease;
                text-transform: uppercase;
                font-weight: 600;
                font-size: 11px;
                letter-spacing: 0.5px;

                &:hover {
                  transform: scale(1.05);
                  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                }
              }

              .v-chip {
                font-weight: 600;
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
                transition: all 0.2s ease;

                &:hover {
                  transform: scale(1.05);
                  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
                }
              }

              img {
                border-radius: 8px;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
                transition: transform 0.3s ease;

                &:hover {
                  transform: scale(1.05);
                }
              }

              .v-icon {
                color: #1663a3;
                transition: all 0.2s ease;
                cursor: pointer;

                &:hover {
                  color: #0d4a8a;
                  transform: scale(1.2);
                }
              }
            }
          }

          tr.selected {
            background: linear-gradient(135deg,
                #d4dff0 0%,
                #c9d8e8 100%) !important;
          }
        }

        .v-data-table__selected {
          background: #d4dff0 !important;
        }
      }
    }
  }

  /* Empty State */
  .empty-state {
    border: 2px dashed #d4dff0;
    border-radius: 12px;
    background: linear-gradient(135deg, #f9fbff 0%, #f5f8ff 100%);
    transition: all 0.3s ease;

    &:hover {
      border-color: #1663a3;
      box-shadow: 0 6px 16px rgba(22, 99, 163, 0.1);
    }
  }

  /* Action Buttons */
  .action-button {
    transition: all 0.3s ease;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    font-size: 12px;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15) !important;
    }

    &:active {
      transform: translateY(-1px);
    }
  }

  /* Speed Dial */
  .speed-dial-btn {
    box-shadow: 0 6px 20px rgba(22, 99, 163, 0.3);
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 8px 28px rgba(22, 99, 163, 0.4);
    }
  }

  .speed-dial-item {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.1);
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
    }
  }

  /* Loading Container */
  .loading-container {
    padding: 40px 0;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  /* Animations */
  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-30px);
    }

    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  /* Utilities */
  .gap-2 {
    gap: 8px;
  }

  .text-decoration-none {
    text-decoration: none;
  }

  /* Scrollbar Styling */
  ::v-deep .v-data-table__wrapper::-webkit-scrollbar {
    height: 8px;
  }

  ::v-deep .v-data-table__wrapper::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }

  ::v-deep .v-data-table__wrapper::-webkit-scrollbar-thumb {
    background: #1663a3;
    border-radius: 4px;

    &:hover {
      background: #0d4a8a;
    }
  }

  /* Responsive */
  @media (max-width: 960px) {
    .header-card {
      margin-bottom: 20px;
    }

    .header-top {
      flex-direction: column;
      align-items: flex-start;
      gap: 14px;
    }

    .header-title-section {
      width: 100%;
    }

    .page-title {
      font-size: 22px;
    }

    .header-actions-top {
      width: 100%;
      justify-content: flex-start;
    }

    .action-btn-primary {
      flex: 1;
    }

    .data-card {
      margin-bottom: 20px;
    }
  }

  @media (max-width: 600px) {
    .page-title {
      font-size: 18px;
    }

    .page-desc {
      font-size: 12px;
    }

    .header-actions-top {
      gap: 8px;
      width: 100%;
    }

    .title-icon-wrapper {
      display: flex;
      padding: 10px;
    }

    .list-header {
      padding: 18px 16px;
    }
  }

  /* Floating Action Buttons */
  .v-btn--floating {
    position: relative;
  }

  .v-speed-dial--bottom {
    bottom: 12%;
  }

  .v-speed-dial--right {
    right: 5%;
  }

  /* Create Event Button */
  @keyframes pulse-glow {
    0% {
      box-shadow: 0 0 0 0 rgba(46, 125, 50, 0.7),
        0 6px 30px rgba(27, 94, 32, 0.6);
    }

    50% {
      box-shadow: 0 0 0 12px rgba(46, 125, 50, 0),
        0 8px 40px rgba(27, 94, 32, 0.8);
    }

    100% {
      box-shadow: 0 0 0 0 rgba(46, 125, 50, 0), 0 8px 40px rgba(27, 94, 32, 0.8);
    }
  }

  .create-event-btn {
    background: linear-gradient(135deg, #43a047 0%, #1b5e20 100%) !important;
    border: 2px solid rgba(255, 255, 255, 0.4) !important;
    box-shadow: 0 6px 30px rgba(27, 94, 32, 0.6),
      inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
    font-weight: 800;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    font-size: 14px !important;
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    overflow: hidden;
    animation: pulse-glow 2s infinite;
    min-width: 160px;
    height: 48px !important;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.3) 50%,
          transparent 100%);
      transition: left 0.5s ease;
    }

    &::after {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background: linear-gradient(45deg,
          transparent 30%,
          rgba(255, 255, 255, 0.1) 50%,
          transparent 70%);
      animation: shimmer-btn 3s infinite;
    }

    &:hover {
      background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%) !important;
      transform: translateY(-6px) scale(1.08);
      box-shadow: 0 12px 45px rgba(27, 94, 32, 0.8),
        inset 0 1px 0 rgba(255, 255, 255, 0.3) !important;
      color: white !important;
      animation: pulse-glow 1s infinite;

      &::before {
        left: 100%;
      }

      .v-icon {
        transform: rotate(180deg) scale(1.3);
      }
    }

    &:active {
      transform: translateY(-3px) scale(0.96);
      box-shadow: 0 4px 20px rgba(27, 94, 32, 0.6) !important;
    }

    .v-icon {
      transition: all 0.3s ease;
      font-size: 22px !important;
    }
  }

  @keyframes shimmer-btn {
    0% {
      transform: translateX(-100%);
    }

    100% {
      transform: translateX(100%);
    }
  }
}
</style>